/**
 * Cohorts joined to their members and orientation pairings, plus the
 * derived display status per pairing (COHORT-SCHEDULING-PLAN Phase 4).
 *
 * Sources:
 *   - `visible_cohorts` / `visible_cohort_members` /
 *     `visible_cohort_assignments` — the new admin-gated entity views,
 *     subscribed in their own isolated batch (an org whose republish
 *     lags gets an empty tab, not a dead portal).
 *   - `cohort_progress` — the long-standing whole-Assignment view, which
 *     now carries `cohortAssignmentId` + `cohortAdopted` for free.
 *
 * `isReady` follows the cohort batch so the page can tell "no cohorts"
 * from "views not served here yet".
 */
import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import { toMillis } from '@/lib/toMillis';
import {
  pairingDisplayStatus,
  type LinkedAssignmentStatus,
  type PairingDisplayStatus,
  type StoredPairingStatus,
  type AssignmentSlice,
} from '@/lib/cohortEntity';

export type PairingRow = {
  cohortAssignmentId: bigint;
  orientationId: string;
  orientationVersionTag: string;
  storedStatus: StoredPairingStatus;
  displayStatus: PairingDisplayStatus;
  assignedByEmail: string;
  assignedAtMillis: number;
  cancelledBy: string | null;
};

export type CohortEntityRow = {
  cohortId: bigint;
  name: string;
  projectId: string;
  archived: boolean;
  createdByEmail: string;
  createdAtMillis: number;
  members: string[];
  pairings: PairingRow[];
};

export function useCohortEntities(projectId?: string): {
  cohorts: CohortEntityRow[];
  /** The relevant slice of cohort_progress for skip-reason derivation. */
  assignmentSlices: AssignmentSlice[];
  isReady: boolean;
} {
  const [cohortRows, cohortsReady] = useTable(
    'minetwin',
    minetwinTables.visible_cohorts,
  );
  const [memberRows] = useTable(
    'minetwin',
    minetwinTables.visible_cohort_members,
  );
  const [pairingRows] = useTable(
    'minetwin',
    minetwinTables.visible_cohort_assignments,
  );
  const [assignmentRows] = useTable('minetwin', minetwinTables.cohort_progress);

  const assignmentSlices = useMemo<AssignmentSlice[]>(
    () =>
      assignmentRows.map((a) => ({
        assignmentId: a.assignmentId,
        email: a.email,
        orientationId: a.orientationId,
        cohortAssignmentId: a.cohortAssignmentId ?? null,
        status: a.status.tag as LinkedAssignmentStatus,
      })),
    [assignmentRows],
  );

  const cohorts = useMemo<CohortEntityRow[]>(() => {
    const statusByPairing = new Map<string, LinkedAssignmentStatus[]>();
    for (const a of assignmentSlices) {
      if (a.cohortAssignmentId == null) continue;
      const key = String(a.cohortAssignmentId);
      const list = statusByPairing.get(key) ?? [];
      list.push(a.status);
      statusByPairing.set(key, list);
    }

    const membersByCohort = new Map<string, string[]>();
    for (const m of memberRows) {
      const key = String(m.cohortId);
      const list = membersByCohort.get(key) ?? [];
      list.push(m.email);
      membersByCohort.set(key, list);
    }

    const pairingsByCohort = new Map<string, PairingRow[]>();
    for (const p of pairingRows) {
      const key = String(p.cohortId);
      const stored = p.status.tag as StoredPairingStatus;
      const list = pairingsByCohort.get(key) ?? [];
      list.push({
        cohortAssignmentId: p.cohortAssignmentId,
        orientationId: p.orientationId,
        orientationVersionTag: p.orientationVersion.tag,
        storedStatus: stored,
        displayStatus: pairingDisplayStatus(
          stored,
          statusByPairing.get(String(p.cohortAssignmentId)) ?? [],
        ),
        assignedByEmail: p.assignedByEmail,
        assignedAtMillis: toMillis(p.assignedAt),
        cancelledBy: p.cancelledBy ?? null,
      });
      pairingsByCohort.set(key, list);
    }
    for (const list of pairingsByCohort.values()) {
      list.sort((a, b) => b.assignedAtMillis - a.assignedAtMillis);
    }

    return cohortRows
      .filter((c) => !projectId || c.projectId === projectId)
      .map((c) => {
        const key = String(c.cohortId);
        return {
          cohortId: c.cohortId,
          name: c.name,
          projectId: c.projectId,
          archived: c.archivedAt != null,
          createdByEmail: c.createdByEmail,
          createdAtMillis: toMillis(c.createdAt),
          members: (membersByCohort.get(key) ?? []).sort(),
          pairings: pairingsByCohort.get(key) ?? [],
        };
      })
      .sort((a, b) => b.createdAtMillis - a.createdAtMillis);
  }, [cohortRows, memberRows, pairingRows, assignmentSlices, projectId]);

  return { cohorts, assignmentSlices, isReady: cohortsReady };
}
